import React from 'react';
import PropTypes from 'prop-types';

/**
 * Container Component
 * 
 * A responsive container component that constrains content to a maximum width
 * and centers it on the page. Provides consistent padding on smaller screens.
 * 
 * @example
 * // Default container
 * <Container>
 *   <h1>Page Content</h1>
 * </Container>
 * 
 * @example
 * // Large container
 * <Container size="xl">
 *   <div>Wide content area</div>
 * </Container>
 * 
 * @example
 * // Container without padding
 * <Container padding={false}>
 *   <div>Full-width content</div>
 * </Container>
 */
const Container = ({
  children,
  size = 'lg',
  padding = true,
  className = '',
  as: Component = 'div',
  ...props
}) => {
  // Size classes (max-width)
  const sizeClasses = {
    sm: 'max-w-screen-sm',     // 640px
    md: 'max-w-screen-md',     // 768px
    lg: 'max-w-screen-lg',     // 1024px
    xl: 'max-w-screen-xl',     // 1280px
    '2xl': 'max-w-screen-2xl', // 1536px
    '7xl': 'max-w-7xl',        // 1280px (80rem)
    full: 'max-w-full',        // 100%
  };
  
  // Padding classes
  const paddingClasses = padding
    ? 'px-4 sm:px-6 lg:px-8'
    : '';
  
  // Center content
  const centerClasses = 'mx-auto w-full';
  
  // Combine all classes
  const combinedClasses = [
    centerClasses,
    sizeClasses[size],
    paddingClasses,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <Component className={combinedClasses} {...props}>
      {children}
    </Component>
  );
};

Container.propTypes = {
  /** Container content */
  children: PropTypes.node.isRequired,
  /** Maximum width size */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', '7xl', 'full']),
  /** Whether to include responsive horizontal padding */
  padding: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** HTML element or React component to render as */
  as: PropTypes.elementType,
};

export default Container;
